// src/features/ucits-etf/components/BreakoutBoard.tsx
//
// The daily quantitative "Breakout Ideas" board. Consumes the
// /ucits-etfs/breakout-ideas envelope (top_buys / top_sells) and splits it
// into FOUR dedicated cards — one per actionable signal — so each regime is
// read in isolation (never merged):
//
//   BREAKOUTS (BUY)      ← top_buys where signal === 'BUY'
//   SUPPORT (ACCUMULATE) ← top_buys where signal === 'ACCUMULATE'
//   TRIM (REDUCE)        ← top_sells where signal === 'REDUCE'
//   BREAKDOWNS (SELL)    ← top_sells where signal === 'SELL'
//
// The backend excludes HOLD entirely and caps each signal independently (so a
// high-conviction BUY can never starve ACCUMULATE, nor SELL starve REDUCE),
// ranking every signal by conviction — so no client-side sorting is needed.
// Every numeric column uses tabular-nums so decimals line up perfectly, and
// each row renders a conviction bar (0–100) plus the price the signal fired at.

import { ArrowDownRight, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBreakoutIdeas } from '../api/etfQueries';
import { etfInfoPath } from '../routes';
import type { BreakoutIdea, BreakoutSignal } from '../types';

// Per-signal pill styling (used inside the breakdown card to distinguish a
// hard SELL from a softer REDUCE).
const SIGNAL_PILL: Record<BreakoutSignal, string> = {
    BUY: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/40',
    ACCUMULATE:
        'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/40',
    HOLD: 'bg-slate-100 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600',
    REDUCE: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/40',
    SELL: 'bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/40',
};

const formatPrice = (value: number | null, currency: string | null): string => {
    if (value == null) return '—';
    try {
        if (currency) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
                maximumFractionDigits: 2,
            }).format(value);
        }
    } catch {
        /* Unknown currency code — fall through to plain formatting. */
    }
    return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

interface ColumnTheme {
    text: string;
    header: string;
    bar: string;
    count: string;
}

interface IdeaColumnProps {
    title: string;
    ideas: BreakoutIdea[];
    icon: React.ReactNode;
    theme: ColumnTheme;
}

const IdeaColumn = ({ title, ideas, icon, theme }: IdeaColumnProps) => (
    <div className="surface-card flex flex-col h-full !p-0 overflow-hidden">
        <div
            className={`px-6 py-5 border-b border-slate-100 dark:border-slate-700/60 flex items-center justify-between ${theme.header}`}
        >
            <div className="flex items-center gap-3">
                <div className={theme.text}>{icon}</div>
                <h3
                    className={`font-extrabold text-sm tracking-widest uppercase ${theme.text}`}
                >
                    {title}
                </h3>
            </div>
            <span
                className={`text-xs font-bold px-2.5 py-1 rounded-lg bg-white dark:bg-slate-900/70 shadow-sm ${theme.count}`}
            >
                {ideas.length}
            </span>
        </div>

        <div className="p-3 flex-1 overflow-y-auto">
            {ideas.length === 0 ? (
                <div className="m-3 p-10 text-center text-sm font-medium rounded-2xl
                                text-slate-400 dark:text-slate-500
                                border-2 border-dashed border-slate-200 dark:border-slate-700">
                    No signals today
                </div>
            ) : (
                ideas.slice(0, 20).map((idea) => (
                    <div
                        key={`${idea.symbol}-${idea.date}`}
                        className="relative mb-2 p-4 rounded-2xl overflow-hidden flex flex-col group cursor-default transition-all
                                   bg-slate-50/70 dark:bg-slate-900/40
                                   border border-transparent
                                   hover:bg-white dark:hover:bg-slate-800/60
                                   hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-md"
                    >
                        {/* Conviction bar pinned to the bottom edge. */}
                        <div
                            className={`absolute bottom-0 left-0 h-1 ${theme.bar} transition-all`}
                            style={{
                                width: `${Math.min(Math.max(idea.conviction_score, 0), 100)}%`,
                            }}
                        />

                        <div className="flex justify-between items-start gap-3 mb-3">
                            <div className="min-w-0">
                                <Link
                                    to={etfInfoPath(idea.symbol)}
                                    title={`Open ${idea.symbol} deep-dive`}
                                    className={`font-bold text-slate-900 dark:text-slate-100 group-hover:${theme.text} hover:underline transition-colors`}
                                >
                                    {idea.symbol}
                                </Link>
                                <div
                                    className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[9rem]"
                                    title={idea.name}
                                >
                                    {idea.name}
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <div className="font-mono text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
                                    {formatPrice(idea.price_at_signal, idea.currency)}
                                </div>
                                <div className="text-[9px] uppercase font-bold tracking-widest mt-0.5 text-slate-400 dark:text-slate-500">
                                    Signal Price
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-2">
                            <span
                                className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border ${SIGNAL_PILL[idea.signal]}`}
                            >
                                {idea.signal}
                            </span>
                            <span className="font-mono text-xs font-bold tabular-nums text-slate-600 dark:text-slate-300">
                                {idea.conviction_score.toFixed(1)}
                            </span>
                        </div>
                    </div>
                ))
            )}
        </div>
    </div>
);

const BoardSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
            <div key={i} className="surface-card-skeleton h-96" />
        ))}
    </div>
);

export const BreakoutBoard = () => {
    const { data, isLoading, isError } = useBreakoutIdeas();

    if (isLoading) return <BoardSkeleton />;

    if (isError) {
        return (
            <div className="surface-card-error">
                Unable to load breakout ideas right now. Please try again
                shortly.
            </div>
        );
    }

    if (!data) return null;

    // Each actionable signal gets its OWN card — BUY / ACCUMULATE are isolated
    // out of top_buys, SELL / REDUCE out of top_sells. Nothing is merged.
    const buyIdeas = data.top_buys.filter((i) => i.signal === 'BUY');
    const accumulateIdeas = data.top_buys.filter(
        (i) => i.signal === 'ACCUMULATE',
    );
    const reduceIdeas = data.top_sells.filter((i) => i.signal === 'REDUCE');
    const sellIdeas = data.top_sells.filter((i) => i.signal === 'SELL');

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <IdeaColumn
                title="Breakouts · Buy"
                ideas={buyIdeas}
                icon={<TrendingUp size={20} />}
                theme={{
                    text: 'text-emerald-600 dark:text-emerald-400',
                    header: 'bg-emerald-50 dark:bg-emerald-500/10',
                    bar: 'bg-emerald-500',
                    count: 'text-emerald-600 dark:text-emerald-400',
                }}
            />
            <IdeaColumn
                title="Support · Accumulate"
                ideas={accumulateIdeas}
                icon={<Target size={20} />}
                theme={{
                    text: 'text-blue-600 dark:text-blue-400',
                    header: 'bg-blue-50 dark:bg-blue-500/10',
                    bar: 'bg-blue-500',
                    count: 'text-blue-600 dark:text-blue-400',
                }}
            />
            <IdeaColumn
                title="Trim · Reduce"
                ideas={reduceIdeas}
                icon={<ArrowDownRight size={20} />}
                theme={{
                    text: 'text-amber-600 dark:text-amber-400',
                    header: 'bg-amber-50 dark:bg-amber-500/10',
                    bar: 'bg-amber-500',
                    count: 'text-amber-600 dark:text-amber-400',
                }}
            />
            <IdeaColumn
                title="Breakdowns · Sell"
                ideas={sellIdeas}
                icon={<TrendingDown size={20} />}
                theme={{
                    text: 'text-rose-600 dark:text-rose-400',
                    header: 'bg-rose-50 dark:bg-rose-500/10',
                    bar: 'bg-rose-500',
                    count: 'text-rose-600 dark:text-rose-400',
                }}
            />
        </div>
    );
};

export default BreakoutBoard;
